<template>
  <AppScreen :title="title">
    <template v-if="detail && status === 'parsed' && !duplicate" #subtitle>
      <button
        class="place-line"
        :class="{ missing: !place }"
        type="button"
        :disabled="locked"
        @click="askPlace(false)"
      >
        <span>{{ placeLine }}</span>
        <IconChevronRight class="chevron" aria-hidden="true" />
      </button>
    </template>

    <ScreenSkeleton v-if="!detail && !gone && phase === 'loading'" :groups="[30, 72, 64, 70, 58]" />

    <ScreenState
      v-else-if="gone"
      kind="attention"
      :title="t('receipt.gone.title')"
      :body="t('receipt.gone.body')"
    >
      <template #action>
        <AppButton @click="goUp">{{ t('receipt.gone.action') }}</AppButton>
      </template>
    </ScreenState>

    <ScreenState
      v-else-if="!detail && phase === 'error'"
      kind="error"
      :title="t('receipt.review.error.title')"
      :body="t('receipt.review.error.body')"
      @retry="retry"
    />
    <ScreenState
      v-else-if="!detail && phase === 'offline'"
      kind="offline"
      tone="warn"
      :title="t('receipt.review.error.title')"
      :body="t('receipt.review.offline')"
    />

    <template v-else-if="detail">
      <!-- The kit's strip, so the frame hears the screen say it (MOL-349, adversarial А1): drawn in
           place, it stood under the notice «Работаем без связи», two clouds. Its kind is the failure's
           (MOL-181, adversarial Б2): «Без связи» of a server that answered 500 was untrue. -->
      <StatusStrip
        v-if="stale === 'offline' || stale === 'error'"
        class="memory"
        :kind="stale === 'offline' ? 'offline' : 'unanswered'"
        :text="t(stale === 'offline' ? 'receipt.review.offline' : 'receipt.review.unanswered')"
      />

      <!-- Recorded elsewhere: the screen of a receipt exists only before it (handoff 05). -->
      <template v-if="status === 'recorded'">
        <ScreenState
          kind="attention"
          inline
          :title="t('receipt.gone.title')"
          :body="t('receipt.gone.body')"
        />
      </template>

      <!-- «Уже записан» (06): the same receipt — tax number and number — recorded before. -->
      <template v-else-if="duplicate">
        <ScreenState
          kind="attention"
          inline
          :title="t('receipt.duplicate.title', { day: dayOf(duplicate.recordedAt) })"
          :body="t('receipt.duplicate.body')"
        />
        <AppCard class="block" list>
          <ListRow
            :icon="IconReceiptCheck"
            tone="good"
            :title="detail.receipt.place?.name ?? title"
            :meta="
              t('purchases.recorded_receipt', {
                count: positions(detail.lines.length),
                day: dayOf(duplicate.recordedAt),
              })
            "
            wrap
            next
            @click="openTrip(duplicate.tripId)"
          />
        </AppCard>
      </template>

      <!-- «Не разобран» (06): the parts from this phone, top to bottom; «переснимите» says why. -->
      <template v-else-if="status === 'failed'">
        <!-- A receipt by its link (MOL-232): the tax office's word, and no photo to show. -->
        <ScreenState
          v-if="byLink"
          kind="attention"
          inline
          :title="t(`receipt.failed.link.${linkFailure}_title`)"
          :body="t(`receipt.failed.link.${linkFailure}_body`)"
        />
        <ScreenState
          v-else
          kind="attention"
          inline
          :title="t(reshoot ? 'receipt.failed.reshoot_title' : 'receipt.failed.title')"
          :body="
            t(
              canRetake
                ? reshoot
                  ? 'receipt.failed.reshoot_body'
                  : 'receipt.failed.body'
                : 'receipt.failed.manual_body',
            )
          "
        />
        <template v-if="!byLink">
          <SectionCaption as="p" class="caption">{{ t('receipt.failed.photos') }}</SectionCaption>
          <p v-if="photos.length === 0" class="note">{{ t('receipt.failed.photos_elsewhere') }}</p>
          <figure v-for="(url, index) in photos" :key="url" class="part">
            <figcaption class="part-caption">
              {{ t('receipt.capture.part_of', { n: index + 1, total: photos.length }) }}
            </figcaption>
            <img :src="url" alt="" class="part-photo" loading="lazy" decoding="async" />
          </figure>
        </template>
        <AppButton variant="danger-ghost" block class="delete" @click="remove">
          <template #icon><IconDelete /></template>
          {{ t('purchases.delete') }}
        </AppButton>
      </template>

      <!-- Still being read: opened by a link before its time. -->
      <template v-else-if="status !== 'parsed'">
        <ScreenState
          kind="attention"
          inline
          :title="t('purchases.group_working')"
          :body="t(byLink ? 'purchases.queued.link_parsing' : 'purchases.queued.parsing')"
        />
      </template>

      <template v-else>
        <p v-if="refused" class="refused" role="alert">{{ refused }}</p>
        <!-- A sole trader's section with no items (MOL-227): the sum is what there is to record;
             the purchases, if wanted, are added later in the trip (В-1). -->
        <AppNote v-if="noItems" class="no-items">
          {{ t('receipt.review.no_items') }}
          <!-- A receipt with items whose every mark OCR lost is read so too (Р-7): a new shot is a tap
               away, as on «Прочитали не всё» (adversarial А6). -->
          <AppButton v-if="canRetake && !locked" variant="ghost" @click="retake">
            <template #icon><IconCamera /></template>
            {{ t('receipt.capture.retake') }}
          </AppButton>
        </AppNote>
        <p v-else class="caption-plain">
          {{ t('receipt.review.count_hint', { count: positions(lines.length) }) }}
          <template v-if="checks > 0">
            · {{ t('receipt.review.issues_check', { n: checks }) }}</template
          >
        </p>
        <!-- Lines from the tax office (MOL-232): nothing of OCR to check against the paper. -->
        <AppNote v-if="byLink" class="source">{{ t('receipt.review.from_tax_office') }}</AppNote>
        <!-- Read in part (MOL-222, В-1): what was «переснимите» — said, and recorded all the same. -->
        <AppNote v-else-if="partly" tone="warn" class="partly">
          {{ partly }}
          <AppButton v-if="canRetake && !locked" variant="ghost" @click="retake">
            <template #icon><IconCamera /></template>
            {{ t('receipt.capture.retake') }}
          </AppButton>
        </AppNote>
        <AppCard class="block" list>
          <ReceiptLineRow
            v-for="one in lines"
            :key="one.added ?? one.position"
            :line="one"
            :lang="lang"
            @open="!locked && openLine(one.position)"
          />
          <!-- A line the reader lost, typed from the paper (MOL-273, В-2 «а»): last in the card, by the
               foot that says what the lines lack. -->
          <ListRow
            class="add-line"
            :icon="IconPlus"
            :title="t('receipt.added.add')"
            :meta="addedFull ? t('receipt.added.full', { max: RECEIPT_ADDED_MAX }) : ''"
            :inactive="locked || addedFull"
            next
            @click="!locked && !addedFull && openAdded(null)"
          />
        </AppCard>
        <ReceiptTotal
          class="block"
          :balance="balance"
          :total="shownTotal"
          :cashback="detail.cashback ?? null"
          :redeemed="detail.redeemed ?? null"
          :rate="detail.rate"
          :day="rateDay"
          :today="today"
          :suspect="suspect"
          :no-items="noItems"
          @total="!locked && (totalOpen = true)"
        />
        <!-- The account it was paid from (MOL-312): a draft of the review, recorded with «Записать». Grown in
             where «Счета» answer after the review, as it pushes what stands under it (review Р1-3). -->
        <AppReveal>
          <AppCard v-if="showsAccount" ref="paymentCard" class="block account">
            <AccountRow
              :label="t('accounts.picker.row_spending')"
              :account="account"
              :disabled="locked"
              @open="pickerOpen = true"
            />
            <AppReveal>
              <ChargedField
                v-if="charged && account"
                v-model="debited"
                :account="account"
                :bad="debitedBad"
                :readonly="locked"
                @update:model-value="debitedBad = false"
              />
            </AppReveal>
          </AppCard>
        </AppReveal>
        <p v-if="!byLink" class="note">
          <IconImageOff class="note-icon" aria-hidden="true" />
          {{ t(noItems ? 'receipt.review.photo_note_sum' : 'receipt.review.photo_note') }}
        </p>
        <AppButton v-if="shelved.length > 0" variant="ghost" block @click="toDeveloper">
          <template #icon><IconMessage /></template>
          {{ t('receipt.review.to_developer') }}
        </AppButton>
        <!-- Not while «Записать» waits: the purchases are on their way, and a removal behind them
             would meet a recorded receipt (adversarial А1). -->
        <AppButton v-if="!locked" variant="danger-ghost" block class="delete" @click="remove">
          <template #icon><IconDelete /></template>
          {{ t('purchases.delete') }}
        </AppButton>
      </template>
    </template>

    <template v-if="detail && docked" #docked>
      <template v-if="docked === 'record'">
        <template v-if="recording">
          <p class="under">{{ t('receipt.review.recording') }}</p>
          <AppButton
            v-if="cancellable"
            variant="ghost"
            block
            :busy="checking"
            :busy-label="t('receipt.review.cancel_record_busy')"
            @click="cancelRecord"
          >
            {{ t('receipt.review.cancel_record') }}
          </AppButton>
          <p v-if="cancelRefused" class="under">
            {{
              t(
                cancelRefused === 'offline'
                  ? 'receipt.review.cancel_record_offline'
                  : 'receipt.review.cancel_record_failed',
              )
            }}
          </p>
        </template>
        <AppButton
          v-else
          size="large"
          block
          :busy="sending"
          :busy-label="t('receipt.review.record_busy')"
          :inactive="noItems ? shownTotal === null : balance.recorded === 0"
          @click="record()"
        >
          {{ recordLabel }}
        </AppButton>
        <p v-if="!online && !recording" class="under">{{ t('receipt.review.record_offline') }}</p>
      </template>
      <!-- A record by hand is everyone's; a retake is the camera's, the country's that reads
           receipts — a receipt taken before a move to Georgia or Serbia is retaken nowhere (MOL-109, Б3). -->
      <template v-else-if="docked === 'failed'">
        <ManualEntryButton />
        <AppButton v-if="canRetake" variant="ghost" block @click="retake">
          <template #icon><IconCamera /></template>
          {{ t('receipt.capture.retake') }}
        </AppButton>
        <!-- A till read badly is one to learn (MOL-222, В-2): its photos, seen before they go. -->
        <AppButton v-if="shelved.length > 0" variant="ghost" block @click="toDeveloper">
          <template #icon><IconMessage /></template>
          {{ t('receipt.review.to_developer') }}
        </AppButton>
      </template>
      <AppButton
        v-else-if="docked === 'duplicate'"
        variant="secondary"
        size="large"
        block
        @click="remove"
      >
        <template #icon><IconDelete /></template>
        {{ t('purchases.delete') }}
      </AppButton>
    </template>

    <ReceiptLineSheet
      v-if="openedLine && detail"
      :key="`${detail.receipt.id}-${openedLine.position}-${openings}`"
      :open="lineOpen"
      :line="openedLine"
      :total="lines.length"
      :currency="currency"
      :lang="lang"
      :place="linePlace"
      :country="detail.receipt.country"
      :digits="digits"
      :on-closed="lineClosed"
      @update:open="lineOpen = $event"
      @saved="saveLine"
    />
    <ReceiptAddedLineSheet
      v-if="addedMounted && detail"
      :key="`${detail.receipt.id}-added-${openings}`"
      :open="addedOpen"
      :line="openedAdded"
      :currency="currency"
      :on-closed="addedClosed"
      @update:open="addedOpen = $event"
      @saved="saveAdded"
      @removed="removeAdded"
    />
    <ReceiptPlaceSheet
      v-if="detail"
      v-model:open="placeOpen"
      :country="detail.receipt.country"
      :current="place"
      :proposed="detail.receipt.header?.shop ?? null"
      :read="!!detail.receipt.place && !placeChosen"
      :day="day"
      :sent-day="sentDay"
      :action="recordAfterPlace ? recordLabel : null"
      :on-closed="placeClosed"
      @chosen="choosePlace"
    />
    <ReceiptBarcodesSheet
      v-if="detail && lineCodes.length > 0"
      v-model:open="codesOpen"
      :codes="lineCodes"
      :on-closed="codesClosed"
      @answered="codesAnswered"
    />
    <AccountPickerSheet
      v-if="detail"
      v-model:open="pickerOpen"
      :title="t('accounts.picker.row_spending')"
      :accounts="accounts.accounts"
      :currency="currency"
      :selected="accountId"
      @pick="pickAccount"
    />
    <ReceiptTotalSheet
      v-if="detail"
      v-model:open="totalOpen"
      :currency="currency"
      :current="shownTotal"
      :corrected="!!draft?.total"
      @saved="saveTotal"
    />
    <!-- Mounted until it is put away, as from the strip: the sheet tells «sent» from its `onClosed`,
         and unmounted on `update:open` it told nobody (review 2). -->
    <CaptureSheet
      v-if="canRetake && country && detail && retakeMounted"
      v-model:open="retaking"
      :country="country"
      :replacing="detail.receipt.id"
      :on-closed="() => (retakeMounted = false)"
      @sent="retaken"
    />
  </AppScreen>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import IconCamera from '~icons/mdi/camera-outline'
import IconChevronRight from '~icons/mdi/chevron-right'
import IconDelete from '~icons/mdi/delete-outline'
import IconImageOff from '~icons/mdi/image-off-outline'
import IconMessage from '~icons/mdi/message-text-outline'
import IconPlus from '~icons/mdi/plus'
import IconReceiptCheck from '~icons/mdi/receipt-text-check-outline'
import { ApiError } from '@molvia/client'
import {
  ERROR,
  RECEIPT_ADDED_MAX,
  formatMoney,
  linesTotalOf,
  parseMoney,
  readCovered,
  readPartly,
  receiptCard,
  receiptDigits,
  receiptSourceOf,
  withoutItems,
  yerevanDate,
} from '@molvia/model'
import type { Money, ReceiptCountry, TripPaymentBody } from '@molvia/model'
import AccountPickerSheet from '@/components/AccountPickerSheet.vue'
import AccountRow from '@/components/AccountRow.vue'
import AppButton from '@/components/AppButton.vue'
import AppCard from '@/components/AppCard.vue'
import AppNote from '@/components/AppNote.vue'
import AppReveal from '@/components/AppReveal.vue'
import AppScreen from '@/components/AppScreen.vue'
import CaptureSheet from '@/components/CaptureSheet.vue'
import ChargedField from '@/components/ChargedField.vue'
import { pageOrder } from '@/components/accounts'
import ListRow from '@/components/ListRow.vue'
import ManualEntryButton from '@/components/ManualEntryButton.vue'
import ReceiptLineRow from '@/components/ReceiptLineRow.vue'
import ReceiptBarcodesSheet from '@/components/ReceiptBarcodesSheet.vue'
import ReceiptLineSheet from '@/components/ReceiptLineSheet.vue'
import ReceiptPlaceSheet from '@/components/ReceiptPlaceSheet.vue'
import ReceiptAddedLineSheet from '@/components/ReceiptAddedLineSheet.vue'
import ReceiptTotal from '@/components/ReceiptTotal.vue'
import ReceiptTotalSheet from '@/components/ReceiptTotalSheet.vue'
import ScreenSkeleton from '@/components/ScreenSkeleton.vue'
import ScreenState from '@/components/ScreenState.vue'
import StatusStrip from '@/components/StatusStrip.vue'
import SectionCaption from '@/components/SectionCaption.vue'
import { api } from '@/api'
import { useFieldRefusal } from '@/composables/useFieldRefusal'
import { useOnline } from '@/composables/useOnline'
import { useReceipt } from '@/composables/useReceipt'
import { useReceiptCapture } from '@/composables/useReceiptCapture'
import { useLocalDay } from '@/composables/useLocalDay'
import { calendarDayOfAnyYear, dayOfAnyYear, localDay } from '@/days'
import { reportFailure } from '@/failures'
import { newId } from '@/ids'
import { afterStep, useNavigation } from '@/navigation'
import { photoShelf } from '@/receipts/photoShelf'
import { recordBody, reviewBalance, reviewDay, reviewLines, reviewPlace } from '@/receipts/review'
import { useAccountsStore } from '@/stores/accounts'
import { useActorStore } from '@/stores/actor'
import { useFeedbackSheetStore } from '@/stores/feedbackSheet'
import { receiptSlots, rememberPaymentAccount, rememberedAccount } from '@/stores/paymentAccounts'
import { useReceiptDraftsStore } from '@/stores/receiptDrafts'
import { placeIdOf } from '@/stores/receiptDrafts'
import type { AddedLineDraft, LineDraft, PlaceDraft } from '@/stores/receiptDrafts'
import { useReceiptQueueStore } from '@/stores/receiptQueue'

/** The language a receipt of a country is printed in, for the system's face to pick its glyphs. */
// the language a till prints its lines in, for the browser's choice of glyphs and hyphens
const PRINTED_LANG: Readonly<Record<ReceiptCountry, string>> = { AM: 'hy', RS: 'sr' }

/**
 * A receipt before it is recorded (MOL-127, handoff 05, 06): its place and day, its lines as read
 * with the person's edits over them, the arithmetic of the model (В-6), and «Записать N» — which
 * goes through the queue under a trip the phone names, so a double tap and a repeat are one record.
 * After it the screen gives way to the purchases (`router.replace`): «back» goes to «Покупки», and
 * this screen is in the history no more. Not read, recorded before, removed elsewhere — each its
 * own state; the edits live on the phone and need no connection (Т-9).
 */
export default defineComponent({
  name: 'ReceiptView',
  components: {
    AccountPickerSheet,
    AccountRow,
    AppButton,
    AppCard,
    AppNote,
    AppReveal,
    AppScreen,
    CaptureSheet,
    ChargedField,
    IconCamera,
    IconChevronRight,
    IconDelete,
    IconImageOff,
    IconMessage,
    ManualEntryButton,
    ListRow,
    ReceiptLineRow,
    ReceiptBarcodesSheet,
    ReceiptAddedLineSheet,
    ReceiptLineSheet,
    ReceiptPlaceSheet,
    ReceiptTotal,
    ReceiptTotalSheet,
    ScreenSkeleton,
    ScreenState,
    SectionCaption,
    StatusStrip,
  },
  setup() {
    const { t, locale } = useI18n()
    // The screen's day, asked again as the app comes back: the day words are said from it (MOL-329).
    const today = useLocalDay()
    const route = useRoute()
    const router = useRouter()
    const { goUp } = useNavigation()
    const actor = useActorStore()
    const queue = useReceiptQueueStore()
    const drafts = useReceiptDraftsStore()
    const online = useOnline()
    const { photoCountry: country } = useReceiptCapture()
    const id = computed(() => String(route.params.receiptId ?? '').toLowerCase())
    const kept = useReceipt(id)
    const detail = computed(() => kept.answer.value)
    const draft = computed(() => drafts.draftOf(id.value))

    const status = computed(() => detail.value?.receipt.status ?? null)
    const duplicate = computed(() =>
      status.value === 'parsed' ? (detail.value?.duplicateOf ?? null) : null,
    )
    const reshoot = computed(() => detail.value?.receipt.failure === 'reshoot')
    /** A Serbian receipt by its link (MOL-232): its lines are the tax office's, there is no photo. */
    const byLink = computed(() =>
      detail.value ? receiptSourceOf(detail.value.receipt.country) === 'tax' : false,
    )
    const linkFailure = computed(() => {
      const failure = detail.value?.receipt.failure
      return failure === 'missing' || failure === 'invalid' ? failure : 'unreadable'
    })
    // A retake is the camera's, of a photo: a receipt by its link is pasted again, not shot
    const canRetake = computed(() => country.value !== null && !byLink.value)
    /** The lines typed on the review (MOL-273): a receipt with no items takes purchases too. */
    const added = computed(() => drafts.addedOf(id.value))
    // the server takes at most so many: one past it would refuse the whole «Записать» (self-review №4)
    const addedFull = computed(() => added.value.length >= RECEIPT_ADDED_MAX)
    const noItems = computed(() =>
      detail.value
        ? added.value.length === 0 &&
          withoutItems({
            status: detail.value.receipt.status,
            lineCount: detail.value.lines.length,
          })
        : false,
    )
    const lang = computed(() => (detail.value ? PRINTED_LANG[detail.value.receipt.country] : 'hy'))
    const currency = computed(() => receiptCard(detail.value?.receipt.country ?? 'AM').currency)
    const taken = computed(() => localDay(detail.value?.receipt.capturedAt ?? new Date()))

    const lines = computed(() =>
      detail.value
        ? reviewLines(detail.value, draft.value, drafts.shownOf(id.value), added.value)
        : [],
    )
    const balance = computed(() =>
      detail.value
        ? reviewBalance(detail.value, lines.value, draft.value)
        : { lines: null, difference: null, suspect: null, recorded: 0, blank: 0 },
    )
    const place = computed(() => (detail.value ? reviewPlace(detail.value, draft.value) : null))
    const placeChosen = computed(() => !!draft.value?.place)
    const day = computed(() =>
      detail.value ? reviewDay(detail.value, draft.value, taken.value) : taken.value,
    )
    // no date of the receipt — none read, or none it could print (MOL-305): the day it was sent stands in, said as
    // such wherever the day is shown while it is that day — a «Готово» that left it so chose nothing
    const sentDay = computed(() => (detail.value?.receipt.header?.date ? null : taken.value))
    const shownTotal = computed<Money | null>(
      () => draft.value?.total ?? detail.value?.receipt.total ?? null,
    )
    const suspect = computed(() => {
      const at = balance.value.suspect
      return at === null ? null : (lines.value[at]?.name ?? null)
    })
    // «проверьте» of an item or of figures the font's swaps put right (review round 1, №2)
    const checks = computed(
      () => lines.value.filter((one) => (one.check || one.repaired) && !one.skip).length,
    )
    const recordLabel = computed(() =>
      noItems.value
        ? t('receipt.review.record_sum')
        : t('receipt.review.record', { n: balance.value.recorded }, balance.value.recorded),
    )
    /** The digits the receipt prints its sums to (П-2): the line's «кол-во × цена» is rounded so. */
    const digits = computed(() => {
      const one = detail.value
      if (!one) return 0
      return receiptDigits(currency.value, [
        one.receipt.total,
        ...one.lines.flatMap((line) => [line.price, line.sum, line.discount]),
      ])
    })
    /**
     * «≈ … по курсу» names the day of the rate the server sent, never the day chosen (review 19) —
     * in the rate's own zone, as every rate is printed: `asOf` is Yerevan's midnight, the evening
     * before in UTC, and a phone west of it would say the day before (review 32).
     */
    const rateDay = computed(() => {
      const asOf = detail.value?.rate?.asOf
      return asOf ? yerevanDate(asOf) : day.value
    })

    /**
     * The account the receipt was paid from (MOL-312): the person's choice on this review, else the one last
     * recorded for a receipt paid the same way, else the first of its currency (`rememberedAccount`). A choice
     * of an account this phone no longer knows gives way to the default: the row says what is sent.
     */
    const accounts = useAccountsStore()
    void accounts.refresh()
    const slots = computed(() => receiptSlots(detail.value?.paidBy))
    const payment = computed(() => drafts.paymentOf(id.value))
    const accountId = computed(() => {
      const chosen = payment.value
      if (
        chosen &&
        (chosen.accountId === null || accounts.accounts.some((one) => one.id === chosen.accountId))
      )
        return chosen.accountId
      return rememberedAccount(accounts.accounts, currency.value, slots.value, actor.id)?.id ?? null
    })
    const account = computed(
      () => accounts.accounts.find((one) => one.id === accountId.value) ?? null,
    )
    // no account at all — no row, the review as before accounts (handoff 06)
    const showsAccount = computed(
      () => pageOrder(accounts.accounts).length > 0 || account.value !== null,
    )
    /** «Списано со счёта»: an account of another currency than the receipt's (MOL-43 В-3). */
    const charged = computed(() => !!account.value && account.value.currency !== currency.value)
    const debited = computed({
      get: () => (payment.value?.accountId === accountId.value ? payment.value.debited : ''),
      set: (text: string) => {
        drafts.setPayment(id.value, { accountId: accountId.value, debited: text })
      },
    })
    const debitedBad = ref(false)
    // «Списано» refused at «Записать» is heard (MOL-235, MOL-333): on the screen, in the app's region.
    const paymentCard = ref<{ $el: Element } | null>(null)
    const refuse = useFieldRefusal()
    const pickerOpen = ref(false)
    function pickAccount(next: string | null): void {
      debitedBad.value = false
      // a choice is kept even where it is the default: the default moves with the next receipt recorded, the
      // choice must not (adversarial А5)
      drafts.setPayment(id.value, {
        accountId: next,
        debited: next === accountId.value ? debited.value : '',
      })
    }
    /** What «Записать» sends of the account: none, or the account with «списано» where it applies. */
    function paymentBody(): TripPaymentBody | 'bad' | undefined {
      if (accountId.value === null) return undefined
      const held = account.value
      const text = debited.value.trim()
      if (!held || !charged.value || !text) return { accountId: accountId.value }
      try {
        const value = parseMoney(text, held.currency)
        return value.minor > 0n ? { accountId: accountId.value, debited: value } : 'bad'
      } catch {
        return 'bad'
      }
    }

    const dayOf = (at: Date | string) =>
      typeof at === 'string'
        ? calendarDayOfAnyYear(at, locale.value, today.value)
        : dayOfAnyYear(at, locale.value, today.value)
    const positions = (n: number): string => t('trip.items_count', { n }, n)

    const title = computed(() => {
      const one = detail.value
      if (!one) return t('receipt.review.title_loading')
      if (place.value) return place.value.name
      // the day it was sent is no date of the receipt, and the title does not say it is (MOL-305, Р2-А4)
      return sentDay.value === day.value
        ? t('receipt.review.title_sent', { day: dayOf(day.value) })
        : t('receipt.review.title_no_place', { day: dayOf(day.value) })
    })
    const placeLine = computed(() => {
      const one = detail.value
      if (!one || !place.value) return t('receipt.review.place_missing')
      const tin = one.receipt.header?.tin
      const time = one.receipt.header?.time
      const sent = sentDay.value === day.value
      const words = {
        city: place.value.city,
        inn: tin ?? '',
        day: sent ? t('receipt.review.sent_day', { day: dayOf(day.value) }) : dayOf(day.value),
        time: time ?? '',
      }
      if (!tin) return t('receipt.review.place_line_no_tin', words)
      // a Serbian seller's tax number is its ПИБ (MOL-232)
      const kind = byLink.value ? 'place_line_pib' : 'place_line'
      return time && !placeChosen.value && !sent
        ? t(`receipt.review.${kind}_time`, words)
        : t(`receipt.review.${kind}`, words)
    })

    // The record waiting in the queue, and a refusal of one: the screen is the place to put it right.
    const recording = computed(() =>
      queue.pending.some((write) => write.kind === 'record' && write.id === id.value),
    )
    const refused = computed(() => {
      const refusal = queue.rejected.find(
        (item) => item.write.kind === 'record' && item.write.id === id.value,
      )
      return refusal ? t('receipt.review.refused', { reason: t(refusal.code) }) : null
    })
    /** «Отменить запись» (MOL-169, В-5): any record still waiting, never the one a send carries. */
    const cancellable = computed(
      () => recording.value && !sending.value && queue.carrying !== id.value,
    )
    const checking = ref(false)
    /** Why a begun record was not cancelled: no connection, or the server did not answer (MOL-19). */
    const cancelRefused = ref<'offline' | 'error' | null>(null)
    /**
     * A record whose send has begun may have landed with its answer lost (adversarial А1–А4): opened
     * as if it had not, the review offered «Удалить» on a recorded receipt and edits that would never
     * be written. So it is cancelled only on this check's own answer — never a read that set out
     * before it and came back meanwhile (round 2, Б2) — asked once no send of it is on its way here
     * (`cancelChecked`, Б1) nor running on the server (`receiptSettled`, Г1). Recorded, the screen
     * goes to its purchases; no answer, nothing opens.
     */
    async function cancelRecord(): Promise<void> {
      const asked = id.value
      cancelRefused.value = null
      if (!queue.recordBegun(asked)) {
        queue.cancelRecord(asked)
        return
      }
      checking.value = true
      const settled: { tripId: string | null } = { tripId: null }
      try {
        await queue.cancelChecked(asked, async () => {
          // Answered once no «Записать» of it is still running on the server either — one the phone
          // gave up on by its timeout included (Г1).
          settled.tripId = (await api.receiptSettled(asked)).tripId
          return settled.tripId === null
        })
        // Recorded: its purchases on this answer alone — a read of the receipt after it could fail on
        // the same connection and leave the tap unanswered (round 4, Д1).
        if (settled.tripId !== null && id.value === asked) toPurchases(asked, settled.tripId)
      } catch (error) {
        if (id.value !== asked) return
        if (error instanceof ApiError && error.answered && error.code === ERROR.NOT_FOUND) {
          void kept.retry()
          return
        }
        reportFailure(error, 'screen')
        cancelRefused.value = navigator.onLine ? 'error' : 'offline'
      } finally {
        checking.value = false
      }
    }
    // Said until the connection or the receipt changes: either may open the way (round 2, review 4).
    watch([online, () => detail.value], () => {
      cancelRefused.value = null
    })
    /**
     * While «Записать» waits in the queue — or is being sent — the receipt is what was sent: an edit
     * made now would not reach the server (review 5), nor would a removal (adversarial А1).
     */
    const locked = computed(() => recording.value || sending.value)
    const docked = computed(() => {
      if (!detail.value) return null
      if (status.value === 'failed') return 'failed'
      if (duplicate.value) return 'duplicate'
      if (status.value === 'parsed') return 'record'
      return null
    })

    // The photos of a receipt from this phone (Т-4): the server gives none back. Shown for one not read,
    // and kept for «Отправить чек разработчику» on either (MOL-222, В-2).
    const shelved = ref<readonly Blob[]>([])
    const photos = ref<string[]>([])
    function letGo(): void {
      for (const url of photos.value) URL.revokeObjectURL(url)
      photos.value = []
      shelved.value = []
    }
    watch(
      () => [status.value, id.value] as const,
      async ([now, asked]) => {
        letGo()
        const owner = actor.id
        if ((now !== 'failed' && now !== 'parsed') || !owner) return
        const parts = await photoShelf(owner).parts(asked)
        if (status.value !== now || id.value !== asked) return
        shelved.value = parts
        if (now === 'failed') photos.value = parts.map((part) => URL.createObjectURL(part))
      },
      { immediate: true },
    )
    onBeforeUnmount(letGo)

    const feedback = useFeedbackSheetStore()
    function toDeveloper(): void {
      feedback.open({ from: 'receipt' }, shelved.value)
    }

    /**
     * Read in part (MOL-222, В-1): the model's own rule, of what the server read — never of the edits —
     * said with what it read against the printed total, or how many lines added up without one. A total
     * read in one place is not shown, and still measures the lines, as «примерно» (MOL-244). The server's
     * word where it gave one (MOL-255, А6 «а»): it measured the lines as read, before the check emptied
     * any, and an emptied line here cannot be told from a lost one — so with an empty line it is said
     * with no sums, which the lines shown do not make.
     */
    const partly = computed(() => {
      const one = detail.value
      if (!one || one.lines.length === 0) return null
      // the lines make the total less what a card sent to the card (MOL-289)
      const total = linesTotalOf(one.receipt.total ?? one.readTotal ?? null, one.cashback ?? null)
      const blank = one.lines.some((line) => line.sum === null)
      // a receipt read before keeps no word, and no line the check emptied (А5 «а»)
      if (!(one.readPartly ?? (!blank && readPartly(one.lines, total)))) return null
      if (blank) return t('receipt.review.partly_check')
      const covered = readCovered(one.lines, total)
      if (covered !== null && total !== null) {
        return t(
          one.receipt.total === null
            ? 'receipt.review.partly_read_total'
            : 'receipt.review.partly_total',
          {
            lines: formatMoney(covered, locale.value),
            total: formatMoney(total, locale.value),
            // what the lines leave of the total, said with it (MOL-255, В-7 «а»)
            short: formatMoney(
              { minor: total.minor - covered.minor, currency: total.currency },
              locale.value,
            ),
          },
        )
      }
      return t('receipt.review.partly_lines', {
        n: one.lines.filter((line) => line.settled).length,
        total: one.lines.length,
      })
    })

    // Recorded on another phone while this one looked, or by a send whose answer was lost: its
    // purchases are where to go, and nothing of it waits on the phone any more (MOL-169, А2).
    function toPurchases(receiptId: string, tripId: string): void {
      queue.settleRecorded(new Set([receiptId]))
      drafts.forget(receiptId)
      if (actor.id) void photoShelf(actor.id).drop(receiptId)
      void router.replace({ name: 'purchase', params: { tripId } })
    }
    watch(
      () => detail.value?.receipt,
      (receipt) => {
        if (receipt?.status === 'recorded' && receipt.tripId)
          toPurchases(receipt.id, receipt.tripId)
      },
    )

    // The line sheet: mounted per opening, so each line starts from its own figures.
    const opened = ref<number | null>(null)
    const openedLine = computed(() =>
      opened.value === null ? null : (lines.value[opened.value] ?? null),
    )
    const linePlace = computed(() => {
      const chosen = place.value
      if (!chosen) return null
      return { id: placeIdOf(chosen), name: chosen.name, city: chosen.city }
    })
    const lineOpen = ref(false)
    const openings = ref(0)
    function openLine(position: number): void {
      const typed = lines.value[position]?.added
      if (typed !== undefined) {
        openAdded(added.value.find((one) => one.key === typed) ?? null)
        return
      }
      opened.value = position
      openings.value += 1
      lineOpen.value = true
    }

    // The sheet of a line typed (MOL-273): mounted per opening, a new line or one typed before.
    const addedMounted = ref(false)
    const addedOpen = ref(false)
    const openedAdded = ref<AddedLineDraft | null>(null)
    function openAdded(line: AddedLineDraft | null): void {
      openedAdded.value = line
      openings.value += 1
      addedMounted.value = true
      addedOpen.value = true
    }

    const placeOpen = ref(false)
    const recordAfterPlace = ref(false)
    const totalOpen = ref(false)
    const retaking = ref(false)
    const retakeMounted = ref(false)
    const sending = ref(false)
    let recordOnClose = false

    function askPlace(andRecord: boolean): void {
      recordAfterPlace.value = andRecord
      placeOpen.value = true
    }

    /**
     * The codes the tax office gave the lines recorded, which their items do not hold (MOL-234):
     * asked about once at «Записать», never written in silence (owner's В-2 «а»).
     */
    const lineCodes = computed(() =>
      lines.value.flatMap((one) =>
        !one.skip && one.line.code !== undefined
          ? [{ position: one.position, code: one.line.code, name: one.name }]
          : [],
      ),
    )
    const codesOpen = ref(false)
    let codesAnswer: boolean | null = null

    /** `bind` — the person's answer to «Привязать штрихкоды?»; absent, not asked yet. */
    async function record(bind?: boolean): Promise<void> {
      const one = detail.value
      if (!one || sending.value || recording.value) return
      // the place first, then the codes: either sheet brings «Записать» back once it is away
      if (!reviewPlace(one, draft.value)) {
        askPlace(true)
        return
      }
      if (bind === undefined && lineCodes.value.length > 0) {
        codesAnswer = null
        codesOpen.value = true
        return
      }
      const paid = paymentBody()
      if (paid === 'bad') {
        debitedBad.value = true
        void refuse(() =>
          paymentCard.value?.$el.querySelector<HTMLElement>('[aria-invalid="true"]'),
        )
        return
      }
      const barcodes = bind === true ? lineCodes.value.map((code) => code.position) : undefined
      const body = recordBody(one, draft.value, lines.value, newId(), taken.value, barcodes, paid)
      if (!body) {
        askPlace(true)
        return
      }
      sending.value = true
      try {
        queue.record(one.receipt.id, body)
        // the next receipt paid the same way starts on this account (MOL-312, В-1 «б»)
        rememberPaymentAccount(actor.id, currency.value, slots.value, paid?.accountId ?? null)
        if (!navigator.onLine) {
          await goUp()
          return
        }
        await queue.flush()
        // The answer may come after the person left: only this very review gives way to the
        // purchases, never a screen they moved to meanwhile (adversarial А5).
        if (route.name !== 'purchase-receipt' || id.value !== one.receipt.id) return
        const note = queue.recorded.find((done) => done.receiptId === one.receipt.id)
        if (note) {
          drafts.forget(one.receipt.id)
          await router.replace({
            name: 'purchase',
            params: { tripId: note.tripId },
            state: { recorded: note.count, codesHeld: [...note.held] },
          })
          return
        }
        // Held — the server broke or the connection went: it waits in the queue (Р-5).
        if (queue.pending.some((write) => write.kind === 'record' && write.id === one.receipt.id))
          await goUp()
      } finally {
        sending.value = false
      }
    }

    function remove(): void {
      const one = detail.value
      if (!one) return
      queue.remove(one.receipt.id)
      void goUp()
    }

    return {
      t,
      IconReceiptCheck,
      detail,
      accounts,
      accountId,
      account,
      showsAccount,
      charged,
      debited,
      debitedBad,
      paymentCard,
      pickerOpen,
      pickAccount,
      draft,
      phase: kept.phase,
      stale: kept.stale,
      gone: kept.gone,
      retry: () => void kept.retry(),
      status,
      duplicate,
      reshoot,
      noItems,
      recordLabel,
      lang,
      currency,
      lines,
      balance,
      place,
      placeChosen,
      day,
      sentDay,
      shownTotal,
      suspect,
      checks,
      title,
      placeLine,
      dayOf,
      positions,
      recording,
      cancellable,
      checking,
      cancelRefused,
      cancelRecord: () => void cancelRecord(),
      refused,
      docked,
      photos,
      shelved,
      partly,
      toDeveloper,
      online,
      country,
      byLink,
      linkFailure,
      canRetake,
      opened,
      openedLine,
      linePlace,
      lineOpen,
      openings,
      openLine,
      addedMounted,
      addedFull,
      RECEIPT_ADDED_MAX,
      addedOpen,
      openedAdded,
      openAdded,
      addedClosed: () => {
        addedMounted.value = false
        openedAdded.value = null
      },
      saveAdded: (line: AddedLineDraft) => {
        drafts.setAdded(id.value, line)
      },
      removeAdded: (key: string) => {
        drafts.removeAdded(id.value, key)
      },
      IconPlus,
      placeOpen,
      recordAfterPlace,
      totalOpen,
      retaking,
      sending,
      askPlace,
      record,
      remove,
      goUp: () => void goUp(),
      locked,
      digits,
      rateDay,
      today,
      retakeMounted,
      retake: () => {
        retakeMounted.value = true
        retaking.value = true
      },
      // The receipt this one replaced is gone: so is its screen, once the sheet's step has landed.
      retaken: () => {
        afterStep(() => void goUp())
      },
      openTrip: (tripId: string) => void router.push({ name: 'purchase', params: { tripId } }),
      lineClosed: () => {
        opened.value = null
      },
      saveLine: (line: LineDraft) => {
        // with the item the review shows it with now — kept from the line's first edit (MOL-222, В1)
        if (opened.value !== null)
          drafts.setLine(id.value, opened.value, line, openedLine.value?.line.itemId ?? null)
      },
      choosePlace: (chosen: PlaceDraft, purchasedOn: string) => {
        drafts.setPlace(id.value, chosen, purchasedOn)
        recordOnClose = recordAfterPlace.value
      },
      placeClosed: () => {
        recordAfterPlace.value = false
        if (recordOnClose) {
          recordOnClose = false
          // Told from inside the pop of the sheet's step: «Записать» offline goes up at once, and a
          // move made before the step lands is dropped as a second tap (review 34, as `retaken`).
          afterStep(() => void record())
        }
      },
      lineCodes,
      codesOpen,
      codesAnswered: (bind: boolean) => {
        codesAnswer = bind
      },
      codesClosed: () => {
        const bind = codesAnswer
        codesAnswer = null
        // put away with no answer — × or the scrim — is no record (as the place's sheet)
        if (bind !== null) afterStep(() => void record(bind))
      },
      saveTotal: (total: Money | null) => {
        drafts.setTotal(id.value, total)
      },
    }
  },
})
</script>

<style scoped lang="scss">
.place-line {
  display: inline-flex;
  gap: var(--space-1);
  align-items: center;
  min-height: var(--touch-target);
  padding: 0;
  border: 0;
  color: var(--text-muted);
  background: none;
  text-align: left;
  font: inherit;
  font-size: var(--text-callout);
  cursor: pointer;

  &.missing {
    color: var(--accent-ink);
    font-weight: var(--weight-medium);
  }

  &:focus-visible {
    @include focus-ring;
  }
}

.chevron {
  @include icon;

  font-size: var(--icon);
}

.memory {
  margin: 0 0 var(--space-3);
}

.note-icon {
  @include icon;

  font-size: var(--icon-sm);
}

.partly,
.source,
.no-items {
  margin: 0 0 var(--space-3);
}

.refused {
  margin: 0 0 var(--space-3);
  padding: var(--space-3);
  border-radius: var(--radius);
  color: var(--warn-ink);
  background: var(--warn-tint);
  font-size: var(--text-footnote);
  font-weight: var(--weight-medium);
}

.caption-plain,
.note {
  margin: 0 var(--space-1) var(--space-2);
  color: var(--text-muted);
  font-size: var(--text-footnote);
}

.note {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  margin-top: var(--space-6);
}

.caption {
  margin-top: var(--space-6);
}

.block {
  margin-bottom: var(--space-3);
}

.account {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.part {
  margin: 0 0 var(--space-4);
}

.part-caption {
  margin-bottom: var(--space-2);
  color: var(--text-muted);
  font-size: var(--text-footnote);
  font-weight: var(--weight-medium);
}

.part-photo {
  display: block;
  width: 100%;
  height: auto;
  border: var(--hairline) solid var(--border);
  border-radius: var(--radius);
  object-fit: contain;
}

.delete {
  margin-top: var(--space-2);
}

.under {
  margin: 0;
  color: var(--text-muted);
  font-size: var(--text-footnote);
  text-align: center;
}
</style>
